import { faker } from '@faker-js/faker';
import dayjs from 'dayjs';

import {
  EAircraftEventStatus,
  EMaoMaintenanceBadge,
  ENOPOrganType,
} from '#shared';

import { generateCrewDuty } from './crewFixtures';
import { generateAirport, generateLocation } from './locationFixtures';
import { generateMaintenance } from './maintenanceFixtures';
import { generateTrip } from './tripFixtures';

import type {
  AircraftBaseData,
  AircraftCardItem,
  AircraftData,
  AircraftEventLegacy,
  MaoAircraft,
} from '#entities';

export const generateAircraft = (
  aircraftId = faker.number.int({ min: 1 }),
): MaoAircraft => ({
  Id: aircraftId,
  Registration: `${faker.airline.airline().iataCode}${faker.airline.flightNumber({ addLeadingZeros: true })}`,
  Location: generateLocation(aircraftId),
});

export const generateAircraftRawInfo = ({
  crewDutiesCount = 0,
  maintenanceCount = 0,
  maintenanceAogCount = 0,
  tripsCount = 0,
}: {
  tripsCount?: number;
  maintenanceCount?: number;
  maintenanceAogCount?: number;
  crewDutiesCount?: number;
} = {}): AircraftBaseData => {
  const aircraft = generateAircraft();
  return {
    Id: aircraft.Id,
    Registration: aircraft.Registration,
    Trips: faker.helpers.multiple(() => generateTrip({ aircraft }), {
      count: tripsCount,
    }),
    CrewDuties: faker.helpers.multiple(
      () => generateCrewDuty({ AircraftId: aircraft.Id }),
      {
        count: crewDutiesCount,
      },
    ),
    ActiveTrail: [],
    Status: '',
    isOnFlight: false,
    Maintenances: [
      ...((maintenanceCount &&
        faker.helpers.multiple(() => generateMaintenance(aircraft), {
          count: maintenanceCount,
        })) ||
        []),
      ...((maintenanceAogCount &&
        faker.helpers.multiple(
          () => generateMaintenance(aircraft, EMaoMaintenanceBadge.AOG),
          {
            count: maintenanceAogCount,
          },
        )) ||
        []),
    ],
    Location: aircraft.Location,
    metaHashes: {
      locationHash: '',
      dataHash: '',
      trailHash: '',
    },
  };
};

/**
 * Generate AircraftEventLegacy for mock data
 */
const generateAircraftEvent = (
  type: EAircraftEventStatus,
  overrides?: Partial<AircraftEventLegacy>,
): AircraftEventLegacy => {
  const now = Date.now();
  const eventTimeMs = faker.number.int({ min: 0, max: 14 * 60 * 60 * 1000 }); // 0-14 hours
  const startTime = dayjs(now - eventTimeMs);
  const endTime = startTime.add(faker.number.int({ min: 2, max: 8 }), 'hours');

  const formatTime = (ms: number) => {
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  return {
    Id: faker.string.uuid(),
    Type: type,
    EventTimeMs: eventTimeMs,
    EventTimeString: formatTime(eventTimeMs),
    StartTime: startTime.toISOString(),
    StartTimeUnix: startTime.valueOf(),
    EndTime: endTime.toISOString(),
    EndTimeUnix: endTime.valueOf(),
    isActual: true,
    ...overrides,
  };
};

/**
 * Generate AircraftCardItem for AirFleet Tracker widget preview
 */
export const generateAircraftCardItem = (
  type: 'OnGround' | 'OnFly' | 'OnMaintenance' = 'OnGround',
): AircraftCardItem => {
  const baseData = generateAircraftRawInfo({
    crewDutiesCount: 2,
    maintenanceCount: type === 'OnMaintenance' ? 1 : 0,
    tripsCount: type === 'OnFly' ? 1 : 0,
  });

  const airport = generateAirport();
  const location = generateLocation(baseData.Id);

  // Generate events based on type
  const events: AircraftEventLegacy[] = [];

  if (type === 'OnGround') {
    events.push(
      generateAircraftEvent(EAircraftEventStatus.OnDuty),
      generateAircraftEvent(EAircraftEventStatus.OnCrewDuty),
      generateAircraftEvent(EAircraftEventStatus.OffDuty),
    );
  } else if (type === 'OnFly') {
    const organ = faker.helpers.arrayElement([
      ENOPOrganType.Heart,
      ENOPOrganType.Liver,
      ENOPOrganType.Lungs,
    ]);
    events.push(
      generateAircraftEvent(EAircraftEventStatus.OnTrip, {
        Label: `${faker.string.alpha({ length: 4, casing: 'upper' })}-${faker.helpers.arrayElement(['HT', 'LI', 'LU'])}`,
        Organ: { id: faker.string.uuid(), name: organ, startPoint: 0 },
      }),
      generateAircraftEvent(EAircraftEventStatus.OnDuty),
    );
  } else if (type === 'OnMaintenance') {
    const maintenanceType = faker.helpers.arrayElement(['AOG', 'CLN', 'SCH']);
    events.push(
      generateAircraftEvent(EAircraftEventStatus.OnMaintenance, {
        MaintenanceType: maintenanceType,
        MaintenanceName: `${maintenanceType} Maintenance`,
      }),
    );
  }

  const baseCardItem = {
    ...baseData,
    Events: events,
    closestAirport: airport,
  };

  if (type === 'OnGround') {
    return {
      ...baseCardItem,
      Type: 'OnGround' as const,
      Location: location,
      Airport: airport,
    };
  }

  if (type === 'OnFly') {
    const departureAirport = generateAirport();
    const arrivalAirport = generateAirport();
    return {
      ...baseCardItem,
      Type: 'OnFly' as const,
      Location: location,
      IsOnCase: true,
      IsOnFly: true,
      TripLegStart: dayjs().subtract(2, 'hours').toISOString(),
      TripLegStartUnix: dayjs().subtract(2, 'hours').valueOf(),
      TripLegEnd: dayjs().add(1, 'hour').toISOString(),
      TripLegEndUnix: dayjs().add(1, 'hour').valueOf(),
      IsReturnToBase: false,
      Direction: faker.helpers.arrayElement(['north', 'south', 'east', 'west']),
      Airports: [departureAirport, arrivalAirport],
    };
  }

  // OnMaintenance
  return {
    ...baseCardItem,
    Type: 'OnMaintenance' as const,
    Airport: airport,
  };
};

/**
 * Generate full AircraftData for Crew Availability widget preview
 */
export const generateAircraftData = (): AircraftData => {
  const baseData = generateAircraftRawInfo({
    crewDutiesCount: 2,
    tripsCount: 1,
  });

  const airportLastKnown = generateAirport();
  const airportArrival = generateAirport();
  const airportDeparture = generateAirport();

  const now = Date.now();
  const dutyStart = now - 3 * 60 * 60 * 1000; // 3 hours ago
  const dutyEnd = now + 11 * 60 * 60 * 1000; // 11 hours from now

  return {
    ...baseData,
    id: baseData.Id,
    registration: baseData.Registration,
    actualOrgan: faker.helpers.arrayElement([
      ENOPOrganType.Heart,
      ENOPOrganType.Liver,
      ENOPOrganType.Lungs,
      null,
    ]),
    actualLabel: faker.datatype.boolean()
      ? `${faker.string.alpha({ length: 4, casing: 'upper' })}-${faker.helpers.arrayElement(['HT', 'LI', 'LU'])}`
      : undefined,
    tripActual: baseData.Trips[0] || null,
    tripNext: null,
    tripLegActual: null,
    dutyEvents: [],
    dutyRanges: [[dutyStart, dutyEnd]],
    activeAirports: [],
    airports: [],
    aircraftEvents: [],
    maintenanceType: null,
    airportDeparture,
    airportArrival,
    airportLastKnown,
    maintenancePrev: null,
    maintenanceActual: null,
    maintenanceNext: null,
    crewDutyFullActual:
      baseData.CrewDuties.length >= 2
        ? [baseData.CrewDuties[0], baseData.CrewDuties[1]]
        : null,
    crewDutyFullNext: null,
    onTrialCase: false,
  };
};
